import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { FiscalYearEndReading } from '@/api/settings'
import type { FiscalYearEnd } from '@/domain/fiscal-year'
import { FISCAL_YEAR_END_SETTING_KEY } from '@/domain/fiscal-year'
import { apiFetch } from '~/api/client'

// Taken from the domain rather than spelled again, because the card writes the
// key the pin reads: two spellings would leave the year end saved and every
// record still pinned to 31 December.
export {
  DEFAULT_FISCAL_YEAR_END,
  FISCAL_YEAR_END_SETTING_KEY,
  describeFiscalYearEnd,
  fiscalYearEndDate,
  fiscalYearLabel,
} from '@/domain/fiscal-year'
export type { FiscalYearEnd, FiscalYearEndReading }

const FISCAL_YEAR_END_KEY = ['settings', 'fiscal-year-end'] as const

export function useFiscalYearEnd(): UseQueryResult<FiscalYearEndReading> {
  return useQuery({
    queryKey: FISCAL_YEAR_END_KEY,
    queryFn: () => apiFetch<FiscalYearEndReading>('/settings/fiscal-year-end'),
  })
}

/**
 * Nothing already pinned moves, so only what can still be pinned is refetched:
 * the Risk page offers years off this date, and a record on file keeps the day
 * it was read on.
 */
export function useSaveFiscalYearEnd(): UseMutationResult<unknown, Error, FiscalYearEnd> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (value: FiscalYearEnd) =>
      apiFetch(`/settings/keys/${FISCAL_YEAR_END_SETTING_KEY}`, {
        method: 'PUT',
        body: JSON.stringify({ value }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: FISCAL_YEAR_END_KEY })
      void client.invalidateQueries({ queryKey: ['analytics', 'year-ends'] })
    },
  })
}

export const MONTH_CHOICES = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

/**
 * 29 February is left out rather than offered and refused: it is the one day the
 * save rejects, and a menu that offers it invites a household to pick the answer
 * the product will not take.
 */
export function dayChoices(month: number): number[] {
  const length = new Date(Date.UTC(2001, month, 0)).getUTCDate()
  return Array.from({ length }, (_, index) => index + 1)
}
